import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

export type QuoteSort =
  | "activity_desc"
  | "activity_asc"
  | "priority_desc"
  | "pickup_asc"
  | "pickup_desc"
  | "charge_desc"
  | "charge_asc"
  | "cost_desc"
  | "cost_asc"
  | "margin_desc"
  | "margin_asc"
  | "created_desc"
  | "created_asc"
  | "number_desc"
  | "number_asc"
  | "expiry_desc"
  | "expiry_asc"
  | "total_desc"
  | "total_asc";

export interface QuoteCapabilities {
  can_edit: boolean;
  can_delete: boolean;
  can_rerate: boolean;
  can_accept: boolean;
  can_decline: boolean;
  can_convert_to_lading: boolean;
  can_append_leg: boolean;
  can_request_carrier_suggestions: boolean;
}

export interface QuoteAssignment {
  user_uuid: string | null;
  username: string | null;
  assigned_at: string | null;
}

export interface QuotePricingSummary {
  quote_pricing_version_uuid: string | null;
  configured_pricing_version_uuid: string | null;
  integrity_status: string;
  version_number: number | null;
  version_status: string | null;
  pricing_source: string | null;
  currency: string | null;
  cost_currency: string | null;
  currency_mismatch: boolean;
  charge_total_cents: number | null;
  cost_total_cents: number | null;
  margin_amount_cents: number | null;
  margin_percent: number | null;
  charge_complete: boolean;
  cost_complete: boolean;
  margin_complete: boolean;
  customer_reapproval_required: boolean;
  warning_count: number;
  blocker_count: number;
}

export interface QuoteFreightSummary {
  requested_pickup_date: string | null;
  requested_delivery_date: string | null;
  freight_load_type: string | null;
  equipment_uuid: string | null;
  equipment_type: string | null;
  rated_miles: number | null;
  weight: number | null;
  weight_unit: string | null;
  pallet_count: number | null;
  piece_count: number | null;
  linear_feet: number | null;
}

export interface QuoteSelectedCarrier {
  carrier_contact_uuid: string;
  carrier_name: string | null;
  option_status: string | null;
  cost_status: string | null;
  quote_carrier_cost_uuid: string | null;
  cost_total_cents: number | null;
  cost_currency: string | null;
}

export interface CurrentQuoteListItem {
  quote_uuid: string;
  quote_number: number;
  display_quote_number: string;
  source: string;
  status: string;
  effective_status: string;
  quote_mode: "quick" | "detailed" | string;
  customer_status: string;
  assessment_status: string;
  capacity_status: string;
  quote_priority: number;
  customer_contact_uuid: string;
  customer_display_name: string | null;
  quote_date: string;
  effective_date: string;
  expiry_date: string;
  currency: string;
  total_amount_cents: number;
  origin_display: string | null;
  destination_display: string | null;
  conditions_accepted: boolean;
  version: number;
  assignment: QuoteAssignment;
  pricing: QuotePricingSummary;
  freight: QuoteFreightSummary;
  selected_carrier: QuoteSelectedCarrier | null;
  converted_lading_uuid: string | null;
  converted_manifest_uuid: string | null;
  last_activity_at: string;
  created_at: string;
  modified_at: string;
  capabilities: QuoteCapabilities;
}

export interface QuotePricingVersion {
  quote_pricing_version_uuid: string;
  is_current: boolean;
  version_number: number;
  version_status: string;
  pricing_source: string;
  currency: string | null;
  charge_total_cents: number | null;
  cost_total_cents: number | null;
  margin_amount_cents: number | null;
  margin_percent: number | null;
  charge_complete: boolean;
  cost_complete: boolean;
  margin_complete: boolean;
  customer_reapproval_required: boolean;
  freight_fingerprint?: string | null;
  customer_visible_fingerprint?: string | null;
  warnings: unknown[];
  blocking_errors: unknown[];
  assessment_notes: string | null;
  approved_by_user_uuid?: string | null;
  approved_by?: string | null;
  approved_at: string | null;
  superseded_at?: string | null;
  superseded_by_user_uuid?: string | null;
  superseded_reason?: string | null;
  created_by_user_uuid?: string | null;
  created_by?: string | null;
  created_at: string;
  freight_snapshot?: Record<string, unknown>;
  rate_request?: Record<string, unknown>;
  rate_response?: Record<string, unknown>;
}

export interface QuoteLineItem {
  quote_customer_charge_line_item_uuid?: string;
  quote_carrier_cost_line_item_uuid?: string;
  service_name?: string | null;
  service_type?: string | null;
  rate_cents?: number | null;
  rate_unit?: string | null;
  multiplier?: number | null;
  net_amount_cents?: number | null;
  charge_mode?: string | null;
  note?: string | null;
}

export interface QuoteCustomerCharge {
  quote_customer_charge_uuid: string;
  charge_status?: string | null;
  source_kind?: string | null;
  currency_type?: string | null;
  subtotal_cents?: number | null;
  tax_total_cents?: number | null;
  total_cents?: number | null;
  valid_until?: string | null;
  line_items?: QuoteLineItem[];
}

export interface QuoteCarrierCost {
  quote_carrier_cost_uuid: string;
  carrier_contact_uuid?: string | null;
  carrier_name?: string | null;
  cost_kind?: string | null;
  cost_status?: string | null;
  source_kind?: string | null;
  currency_type?: string | null;
  subtotal_cents?: number | null;
  tax_total_cents?: number | null;
  total_cents?: number | null;
  valid_until?: string | null;
  line_items?: QuoteLineItem[];
}

export interface QuoteCarrierOption {
  quote_carrier_option_uuid: string;
  carrier_contact_uuid?: string | null;
  carrier_name?: string | null;
  option_status?: string | null;
  rank_order?: number | null;
  suitability_score?: number | null;
  margin_score?: number | null;
  availability_status?: string | null;
  transit_days?: number | null;
  cost_total_cents?: number | null;
  cost_currency?: string | null;
  notes?: string | null;
}

export interface QuoteRateObservation {
  quote_rate_observation_uuid: string;
  observation_side?: string | null;
  source_kind?: string | null;
  source_provider?: string | null;
  amount_cents?: number | null;
  low_amount_cents?: number | null;
  high_amount_cents?: number | null;
  currency_type?: string | null;
  sample_count?: number | null;
  confidence_score?: number | null;
  observed_at?: string | null;
}

export interface QuoteHistoricalMatch {
  quote_historical_match_uuid: string;
  lading_uuid?: string | null;
  match_tier?: string | null;
  match_score?: number | null;
  historical_cost_cents?: number | null;
  historical_charge_cents?: number | null;
  historical_margin_cents?: number | null;
  historical_margin_percent?: number | null;
  currency_type?: string | null;
  shipment_date?: string | null;
}

export interface QuoteEvent {
  quote_event_uuid: string;
  event_type?: string | null;
  actor_type?: string | null;
  actor_name?: string | null;
  occurred_at?: string | null;
  event_data?: Record<string, unknown>;
}

export interface QuoteAcceptance {
  quote_acceptance_uuid: string;
  acceptance_type?: string | null;
  acceptance_method?: string | null;
  accepted_by?: string | null;
  accepted_at?: string | null;
}

export interface QuoteFreightSnapshot extends QuoteFreightSummary {
  origin_country?: string | null;
  origin_region?: string | null;
  origin_postal_code?: string | null;
  destination_country?: string | null;
  destination_region?: string | null;
  destination_postal_code?: string | null;
  commodity_summary?: string | null;
  service_requirements?: unknown[];
}

export interface QuoteWorkbenchCollections {
  current_pricing_version: QuotePricingVersion | null;
  pricing_versions: QuotePricingVersion[];
  freight_snapshot: QuoteFreightSnapshot | null;
  customer_charges: QuoteCustomerCharge[];
  carrier_costs: QuoteCarrierCost[];
  carrier_options: QuoteCarrierOption[];
  rate_observations: QuoteRateObservation[];
  historical_matches: QuoteHistoricalMatch[];
  acceptances: QuoteAcceptance[];
  events: QuoteEvent[];
  collection_meta: Record<
    string,
    { returned?: number; truncated?: boolean; limit?: number }
  >;
}

export interface CurrentQuoteDetail extends CurrentQuoteListItem {
  conditions: unknown[];
  conditions_accepted_at: string | null;
  conditions_accepted_by: string | null;
  rate_request: Record<string, unknown>;
  rate_response: Record<string, unknown>;
  carrier_suggestions: Record<string, unknown>;
  converted_at: string | null;
  created_by_user_uuid: string | null;
  created_by: string | null;
  workbench: QuoteWorkbenchCollections;
}

export interface QuoteListMeta {
  source: string;
  read_model?: string;
  total: number;
  returned: number;
  limit: number;
  offset: number;
  sort: QuoteSort;
  has_more: boolean;
  next_offset: number | null;
  archive: false;
  scope?: {
    mode?: string;
    customer_scope_count?: number;
  };
}

export interface QuoteListResponse<T> {
  ok: boolean;
  resource: string;
  data: T[];
  meta: QuoteListMeta;
}

export interface QuoteDetailResponse {
  ok: boolean;
  resource: "quote";
  data: CurrentQuoteDetail;
  meta: Record<string, unknown>;
}

export interface QuoteListQuery {
  limit: number;
  offset: number;
  sort?: QuoteSort;
  search?: string;
  status?: string;
  source?: string;
  quote_mode?: string;
  customer_status?: string;
  assessment_status?: string;
  capacity_status?: string;
  customer_contact_uuid?: string;
  assigned_user_uuid?: string;
  unassigned?: boolean;
  priority?: number;
  pickup_from?: string;
  pickup_to?: string;
  expiring_before?: string;
  has_blockers?: boolean;
  converted?: boolean;
  created_from?: string;
  created_to?: string;
}

export interface QuoteConversionResponse {
  ok: boolean;
  resource: "quote_conversion";
  data: {
    quote_uuid: string;
    quote_number: number;
    quote_status: "converted";
    quote_version: number;
    manifest_uuid: string;
    manifest_id: number;
    manifest_version: number;
    lading_uuid: string;
    lading_id: string;
    manifest_leg_sequence: number;
    lading_status: string;
    physical_package_count: number;
    product_row_count: number;
  };
}

@Injectable({ providedIn: "root" })
export class QuotesApiService {
  private readonly endpoint = "/api/quotes/current_quotes.php";

  constructor(private readonly http: HttpClient) {}

  listCurrent(
    query: QuoteListQuery,
  ): Observable<QuoteListResponse<CurrentQuoteListItem>> {
    return this.http.get<QuoteListResponse<CurrentQuoteListItem>>(
      this.endpoint,
      {
        params: this.params({ ...query, store: "current" }),
      },
    );
  }

  downloadCurrentPdf(quoteUuid: string): Observable<Blob> {
    return this.http.get('/api/quotes/current_quotes.php', {
      params: new HttpParams().set('format', 'pdf').set('quote_uuid', quoteUuid),
      responseType: 'blob'
    });
  }

  getCurrent(quoteUuid: string): Observable<QuoteDetailResponse> {
    return this.http.get<QuoteDetailResponse>(this.endpoint, {
      params: this.params({ store: "current", quote_uuid: quoteUuid }),
    });
  }

  verifyCurrentAccess(): Observable<QuoteListResponse<CurrentQuoteListItem>> {
    return this.listCurrent({ limit: 1, offset: 0, sort: "activity_desc" });
  }

  convertCurrent(
    quoteUuid: string,
    expectedQuoteVersion: number,
    idempotencyKey: string,
  ): Observable<QuoteConversionResponse> {
    return this.http.post<QuoteConversionResponse>(
      this.endpoint,
      { expected_quote_version: expectedQuoteVersion },
      {
        params: new HttpParams()
          .set("command", "convert")
          .set("quote_uuid", quoteUuid),
        headers: new HttpHeaders().set("Idempotency-Key", idempotencyKey),
      },
    );
  }

  private params(values: Record<string, unknown>): HttpParams {
    let params = new HttpParams();

    Object.entries(values).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") {
        return;
      }

      params = params.set(key, String(value));
    });

    return params;
  }
}
